import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';
import type { QueryHistory, RankingOptions, UsageData } from './command-ranking';
import type { Command } from './types';
import { getRecentCommandIds, rankCommands, recordQueryPick } from './command-ranking';
export { commandScore } from './command-ranking';
const usageDataKey = 'commandUsageData';
const queryHistoryKey = 'commandQueryHistory';
const queryHistorySchema: z.ZodType<QueryHistory> = z.record(z.string(), z.record(z.string(), z.object({
  uses: z.number(),
  lastUsed: z.number()
})));
const queryHistoryOptions = {
  // A value saved in another shape, by another version or by hand, reads as no history.
  deserializer: (value: string): QueryHistory => {
    const parsed = queryHistorySchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : {};
  }
};
type SetUsageData = (data: UsageData | ((prev: UsageData) => UsageData)) => void;

// The setters of every mounted hook, so recordCommandUsage works outside of the hook context.
// Each writes the same storage key and the others follow it, so any one of them will do. A
// single slot would go empty when a second search box that took it over unmounts.
const usageDataSetters = new Set<SetUsageData>();
type SetQueryHistory = (data: QueryHistory | ((prev: QueryHistory) => QueryHistory)) => void;
const queryHistorySetters = new Set<SetQueryHistory>();

/**
 * Call this whenever a command is used so we can track it. Pass the search that found it, so
 * typing that search again ranks the command first.
 */
export function recordCommandUsage(commandId: string, searchTerm?: string) {
  const [setUsageData] = usageDataSetters;
  if (!setUsageData) {
    console.warn('Command usage tracking not initialized');
    return;
  }
  setUsageData(prevData => {
    const newData = {
      ...prevData
    };
    if (!newData[commandId]) {
      newData[commandId] = {
        count: 0,
        lastUsed: 0
      };
    }
    newData[commandId].count += 1;
    newData[commandId].lastUsed = Date.now();
    return newData;
  });
  const [setQueryHistory] = queryHistorySetters;
  if (searchTerm?.trim() && setQueryHistory) {
    setQueryHistory(history => recordQueryPick(history, searchTerm, commandId, Date.now()));
  }
}

/**
 * Custom hook to fuzzy-search commands with usage-based ranking.
 */
export function useFuzzySearch(commands, searchTerm, options) {
  const $ = _c(14);
  const boostGroup = options?.boostGroup;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [usageData, setUsageData] = useLocalStorage(usageDataKey, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [queryHistory, setQueryHistory] = useLocalStorage(queryHistoryKey, t1, queryHistoryOptions);
  let t2;
  let t3;
  if ($[2] !== setUsageData) {
    t2 = () => {
      usageDataSetters.add(setUsageData);
      return () => {
        usageDataSetters.delete(setUsageData);
      };
    };
    t3 = [setUsageData];
    $[2] = setUsageData;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== setQueryHistory) {
    t4 = () => {
      queryHistorySetters.add(setQueryHistory);
      return () => {
        queryHistorySetters.delete(setQueryHistory);
      };
    };
    t5 = [setQueryHistory];
    $[5] = setQueryHistory;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== boostGroup || $[9] !== commands || $[10] !== queryHistory || $[11] !== searchTerm || $[12] !== usageData) {
    t6 = rankCommands(commands, searchTerm, {
      usageData,
      queryHistory
    }, {
      boostGroup
    });
    $[8] = boostGroup;
    $[9] = commands;
    $[10] = queryHistory;
    $[11] = searchTerm;
    $[12] = usageData;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

/**
 * The ids of the most recently used commands, newest first, leaving out any not used in the last
 * 30 days. Re-renders when usage is recorded.
 */
export function useRecentCommandIds(t0) {
  const $ = _c(4);
  const limit = t0 === undefined ? 5 : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [usageData] = useLocalStorage(usageDataKey, t1);
  let t2;
  if ($[1] !== limit || $[2] !== usageData) {
    t2 = getRecentCommandIds(usageData, limit);
    $[1] = limit;
    $[2] = usageData;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}